// @flow
import React, { Component, useState } from "react";
import download, { FileType } from "common/utils/download";
import {
  CustomPaging,
  FilteringState,
  IntegratedFiltering,
  IntegratedSelection,
  IntegratedSorting,
  PagingState,
  SelectionState,
  SortingState,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  PagingPanel,
  Table,
  TableColumnResizing,
  TableFilterRow,
  TableHeaderRow,
  TableSelection,
} from "@devexpress/dx-react-grid-material-ui";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Menu from "@material-ui/core/Menu";
import Popover from "@material-ui/core/Popover";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import CloudDownload from "@material-ui/icons/CloudDownload";
import YoutubeSearchedFor from "@material-ui/icons/YoutubeSearchedFor";
import { withStyles } from "@material-ui/core/styles";

import LoaderAbsolute from "components/LoaderAbsolute";
import Export from "components/Export";

import TableOptions from "./TableOptions";
import { fetchAll, fetchUnpaginatedData, getExport } from "./api";
import buildParams, {
  buildQueryParams,
  paramsEqual,
  getFilterString,
  getSortString,
} from "common/utils/search";
import { spread } from "axios";
import debounce from "lodash/debounce";

import FilterCellComponent from "./components/FilterCellComponent";
import type { Props, State, ICPCTableColumn, Params, CellProps } from "./types";
import type { Sorting, Filter, SearchParams } from "types/Global";

const styles = (theme) => ({
  root: {
    position: "relative",
  },
  flex: {
    flex: 1,
  },
  exportPopover: {
    padding: 6,
  },
  toolbar: { backgroundColor: theme.palette.background.paper },
  noDataCell: {
    textAlign: "center",
    padding: "40px 0",
  },
});

type TableContainerProps = Props & {
  reload?: boolean,
  onReloadComplete?: () => void,
  onParamsChange?: (SearchParams) => void,
  isUnpaginated?: boolean,
  selectedRows?: Array<number>,
  hideClearFilter?: boolean,
};

class TableContainer extends Component<TableContainerProps, State> {
  cellComponent: any;
  state: State;
  loadData: (boolean) => void;
  refresh: () => void;
  loadExport: (string) => () => void;
  changeCurrentPage: (number) => void;
  changePageSize: (number) => void;
  changeFilters: (Array<Filter>) => void;
  changeSorting: (Array<Sorting>) => void;
  reduceActiveColumnsNames: (Array<ICPCTableColumn>) => Array<string>;
  reduceSortableColumnsNames: (Array<ICPCTableColumn>) => Array<string>;
  handleOptionsClick: (Event) => void;
  handleOptionsClose: () => void;
  handleExportClick: (Event) => void;
  clearFilters: (Event) => void;
  handleExportClose: () => void;
  haveFilter: () => boolean;
  filterActiveColumns: (
    Array<string>,
    Array<ICPCTableColumn>
  ) => Array<ICPCTableColumn>;
  lastParams: ?Params;
  lastUrl: ?string;
  sortableColumns: Array<string>;

  getParameterValue(parameterName) {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get(parameterName);
  }
  setParameterValue(parameterName, parameterValue) {
    const url = new URL(window.location.href);
    const urlParams = new URLSearchParams(url.search);
    urlParams.set(parameterName, parameterValue);
    url.search = urlParams.toString();
    window.history.replaceState(null, "", url.href);
  }
  removeParameter(parameterName) {
    const url = new URL(window.location.href);
    const urlParams = new URLSearchParams(url.search);
    urlParams.delete(parameterName);
    url.search = urlParams.toString();
    window.history.replaceState(null, "", url.href);
  }
  getParsedFilters() {
    const fils = this.getParameterValue("filter");
    if (fils && fils.length > 0) {
      const filtersArray = fils.split("&");

      const defaultFilters = filtersArray.map((filter) => {
        const [columnName, value] = filter.split("#");
        return { columnName, value };
      });

      return defaultFilters;
    }
    return [];
  }
  getParsedSorting() {
    const sor = this.getParameterValue("sort");
    if (sor && sor.length > 0) {
      return sor.split(",").map((sr) => {
        const [columnName, direction] = sr.split(" ");
        return { columnName, direction };
      });
    }
    return [];
  }
  //Ali kanaan
  //start of handling local storage to save the active/none active columns in search tables
  getTableIdentifier() {
    const url = this.props.url.split("/");
    let target = "";
    for (let part of url) {
      if (part == "") continue;
      if (isNaN(part))
        //all the url is an identifier but the parts with ids (ie. /contest/1234/search as 1234 is an id that doesnt identify the table)
        target += part + "_";
      else target += "X_";
    }
    return target;
  }
  handleTableTarget(handleTarget) {
    const key = "columns_filter";
    const root = JSON.parse(localStorage.getItem(key) || "{}");

    const target = this.getTableIdentifier();
    if (root[target] === undefined) root[target] = {};
    handleTarget(root[target]);
    localStorage.setItem(key, JSON.stringify(root));
  }
  updateColumnsCookie(columnName, value) {
    this.handleTableTarget((target) => {
      target[columnName] = value;
    });
  }
  handleColumnsCookies(columns) {
    this.handleTableTarget((target) => {
      for (const column of columns) {
        if (target[column.name] == undefined)
          target[column.name] = column.active;
        column.active = target[column.name];
      }
    });
  }
  // end of handling local storage.
  constructor(props: TableContainerProps) {
    super(props);
    this.cellComponent = this._cellComponent.bind(this);
    this.loadData = debounce(
      props.isUnpaginated ? this.loadUnpaginatedData : this.loadPaginatedData,
      600
    );
    this.refresh = this._refresh.bind(this);
    this.loadExport = this._loadExport.bind(this);
    this.changeSorting = this._changeSorting.bind(this);
    this.changeCurrentPage = this._changeCurrentPage.bind(this);
    this.changePageSize = this._changePageSize.bind(this);
    this.changeFilters = this._changeFilters.bind(this);
    this.reduceActiveColumnsNames = this._reduceActiveColumnsNames.bind(this);
    this.reduceSortableColumnsNames =
      this._reduceSortableColumnsNames.bind(this);
    this.filterActiveColumns = this._filterActiveColumns.bind(this);
    this.handleOptionsClick = this._handleOptionsClick.bind(this);
    this.handleOptionsClose = this._handleOptionsClose.bind(this);
    this.handleExportClick = this._handleExportClick.bind(this);
    this.clearFilters = this._clearFilters.bind(this);
    this.handleExportClose = this._handleExportClose.bind(this);
    this.haveFilter = this._haveFilter.bind(this);
    this.lastParams = null;
    this.lastUrl = null;

    const { columns } = this.props;

    this.handleColumnsCookies(columns);

    this.sortableColumns = this.reduceSortableColumnsNames(columns);
    const activeColumnsNames = this.reduceActiveColumnsNames(columns);
    this.state = {
      activeColumns: this.filterActiveColumns(activeColumnsNames, columns),
      activeColumnsNames,
      rows: [],
      currentPage: 0,
      sorting: this.props.defaultSort || [],
      totalCount: 0,
      loading: this.props.noInit ? false : true,
      downloading: false,
      pageSize: 10,
      allowedPageSizes: [10, 50, 100, 500, 1000],
      filter: {
        filteringFunction: "&",
        filters: [],
      },
      optionsMenuOpen: false,
      optionsMenuAnchorEl: null,
      exportMenuOpen: false,
      exportMenuAnchorEl: null,
      selection: [],
    };
  }

  static defaultProps = {
    title: "",
    elevation: 2,
    isUnpaginated: false,
    selectedRows: [],
  };

  _haveFilter(): boolean {
    for (let activeColumn of this.state.activeColumns) {
      if (activeColumn.filterable) return true;
    }
    return false;
  }

  _reduceActiveColumnsNames(columns: Array<ICPCTableColumn>): Array<string> {
    return columns.reduce((acc: Array<string>, item: ICPCTableColumn) => {
      if (item.active) {
        acc.push(item.name);
      }
      return acc;
    }, []);
  }

  _reduceSortableColumnsNames(columns: Array<ICPCTableColumn>): Array<string> {
    return columns.reduce((acc: Array<string>, item: ICPCTableColumn) => {
      if (item.sortable) {
        acc.push(item.name);
      }
      return acc;
    }, []);
  }

  _filterActiveColumns(
    activeColumnsNames: Array<string>,
    columns: Array<ICPCTableColumn>
  ): Array<ICPCTableColumn> {
    return columns.filter((column) => activeColumnsNames.includes(column.name));
  }

  _changeSorting(sorting: Array<Sorting>) {
    if (sorting.length === 1) {
      const columnName = sorting[0].columnName;
      if (!this.sortableColumns.includes(columnName)) return;
      this.setState({
        sorting,
        selection: [],
      });
      const sortString = getSortString(sorting).split(";")[0].split(":")[1];
      if (sortString && sortString.length != 0)
        this.setParameterValue("sort", sortString);
      else this.removeParameter("sort");
      const { onSelect } = this.props;
      onSelect && onSelect([]);
    }
  }

  _changeCurrentPage(currentPage: number) {
    // do nothing if the same page
    if (this.state.currentPage === currentPage) return;
    this.setState((prevState, props) => {
      return {
        currentPage,
        selection: [],
      };
    });
    const { onSelect } = this.props;
    onSelect && onSelect([]);
  }

  _changePageSize(pageSize: number) {
    // do nothing if the same page size
    if (this.state.pageSize === pageSize) return;
    const totalPages = Math.ceil(this.state.totalCount / pageSize);
    const currentPage = Math.min(this.state.currentPage, totalPages - 1);
    this.setState({
      pageSize,
      currentPage,
      selection: [],
    });
    const { onSelect } = this.props;
    onSelect && onSelect([]);
  }

  _changeFilters(filters: Array<Filter>) {
    this.setState((prevState, props) => {
      return {
        filter: {
          filteringFunction: prevState.filter.filteringFunction,
          filters,
        },
        selection: [],
      };
    });
    const filterString = getFilterString({
      filteringFunction: this.state.filter.filteringFunction,
      filters,
    })
      .split(";")[0]
      .split(":")[1];
    if (filterString && filterString.length > 0)
      this.setParameterValue("filter", filterString);
    else this.removeParameter("filter");
    const { onSelect } = this.props;
    onSelect && onSelect([]);
  }

  componentDidMount() {
    if (!this.props.noInit) this.loadData();
    this.setState((prevState: State, props: TableContainerProps) => {
      return {
        filter: {
          filteringFunction: "&",
          filters: this.getParsedFilters(),
        },
      };
    });
    const sorting = this.getParsedSorting();
    this.setState({
      sorting,
      selection: [],
    });
  }

  componentDidUpdate(prevProps: TableContainerProps, prevState: State) {
    // if noInit -> do not update in case of an empty search (loading all is meaningless)
    if (!this.props.noInit || this.state.filter.filters.length > 0) {
      if (this.props.reload) {
        if (this.props.isUnpaginated) {
          this.loadUnpaginatedData(true);
        } else {
          this.loadPaginatedData(true);
        }
        this.props.onReloadComplete();
      } else {
        this.loadData();
      }
    }
  }

  _loadExport = (type: string) => () => {
    this.setState({ downloading: true });
    const { activeColumnsNames, currentPage, pageSize, sorting, filter } =
      this.state;
    const params = buildParams(
      activeColumnsNames,
      currentPage + 1,
      pageSize,
      filter,
      sorting
    );
    const exportParams = {
      q: params.q,
      type,
    };
    if (this.props.exportUrl)
      getExport(this.props.exportUrl, exportParams)
        .then((resp) => {
          download(resp.data, `${resp.data.fileName}.${FileType[type]}`);
          this.setState({ downloading: false, exportMenuOpen: false });
        })
        .catch(() => this.setState({ downloading: false }));
  };

  _refresh() {
    console.log("refreshing data--------------------");
    this.loadData(true);
  }

  /**
   * If forced is true, it will skip prevProps comparison and force-refresh
   */
  loadPaginatedData = (forced: boolean = false) => {
    const { activeColumnsNames, currentPage, pageSize, sorting, filter } =
      this.state;
    let params = buildParams(
      activeColumnsNames,
      currentPage + 1,
      pageSize,
      filter,
      sorting,
      this.props.functionalAttributes
    );
    params.q = params.q.split(";").reduce((prev, val) => {
      if (val == "" || val.length == 0) return prev + "";
      const parName = val.split(":")[0];
      const urlVal = this.getParameterValue(parName);
      if (urlVal && urlVal.length > 0)
        return prev + parName + ":" + urlVal + ";";
      else {
        this.removeParameter(parName);
        return prev + val + ";";
      }
    }, "");
    const fil = this.getParameterValue("filter");
    if (fil && fil.length > 0 && params.q.indexOf("filter") == -1)
      params.q += "filter:" + fil + ";";
    const sor = this.getParameterValue("sort");
    if (sor && sor.length > 0 && params.q.indexOf("sort") != -1)
      params.q += "sort:" + sor + ";";
    const { url, onParamsChange } = this.props;
    if (
      forced === false &&
      paramsEqual(params, this.lastParams) &&
      url === this.lastUrl
    ) {
      return;
    }
    onParamsChange && onParamsChange(params);
    this.setState({ loading: true });
    fetchAll(url, params)
      .then(
        spread((data, count) => {
          this.setState({
            rows: data.data,
            totalCount: count.data,
            loading: false,
          });
        })
      )
      .catch(() => this.setState({ loading: false }));
    this.lastParams = params;
    this.lastUrl = url;
  };

  loadUnpaginatedData = (forced: boolean = false) => {
    const { activeColumnsNames, sorting, filter } = this.state;
    const { url } = this.props;
    const params = buildQueryParams(
      activeColumnsNames,
      filter,
      sorting,
      this.props.functionalAttributes
    );
    params.q = params.q.split(";").reduce((prev, val) => {
      if (val == "" || val.length == 0) return prev + "";
      const parName = val.split(":")[0];
      const urlVal = this.getParameterValue(parName);
      if (urlVal && urlVal.length > 0)
        return prev + parName + ":" + urlVal + ";";
      else {
        this.removeParameter(parName);
        return prev + val + ";";
      }
    }, "");
    const fil = this.getParameterValue("filter");
    if (fil && fil.length > 0 && params.q.indexOf("filter") == -1)
      params.q += "filter:" + fil + ";";
    const sor = this.getParameterValue("sort");
    if (sor && sor.length > 0 && params.q.indexOf("sort") != -1)
      params.q += "sort:" + sor + ";";
    if (forced === false && url === this.lastUrl) {
      return;
    }

    this.setState({ loading: true });
    fetchUnpaginatedData(url)
      .then((resp) => {
        this.setState({
          rows: resp.data,
          loading: false,
        });
      })
      .catch(() => this.setState({ loading: false }));
    this.lastParams = params;
    this.lastUrl = url;
  };

  _handleOptionsClick(event: Event) {
    this.setState({
      optionsMenuOpen: true,
      optionsMenuAnchorEl: event.currentTarget,
    });
  }

  _handleOptionsClose() {
    this.setState({
      optionsMenuOpen: false,
    });
  }

  _handleExportClick(event: Event) {
    this.setState({
      exportMenuOpen: true,
      exportMenuAnchorEl: event.currentTarget,
    });
  }
  _clearFilters(event: Event) {
    this.removeParameter("filter");
    this.removeParameter("sort");
    this.setState((prevState: State, props: TableContainerProps) => {
      return {
        filter: {
          filteringFunction: "&",
          filters: [],
        },
        sorting: [],
      };
    });
  }

  _handleExportClose() {
    this.setState({
      exportMenuOpen: false,
    });
  }

  _cellComponent(cell: CellProps) {
    // 1. if a table cell template builder is provided via props
    if (this.props.template) {
      // 1a. get component
      const comp = this.props.template(cell);
      // 1b. if that component is not specified in template, return default Table.Cell
      if (comp === undefined)
        return (
          <Table.Cell
            {...cell}
            data-test={`row-${cell.row.index}-${cell.column.name}`.toLowerCase()}
          />
        );
      // 1c. otherwise return that custom component
      return React.cloneElement(comp, {
        "data-test": `row-${cell.row.index}-${cell.column.name}`.toLowerCase(),
        ...comp.props,
      });
    } else {
      // 2. template not specified, return default
      return (
        <Table.Cell
          {...cell}
          data-test={`row-${cell.row.index}-${cell.column.name}`.toLowerCase()}
        />
      );
    }
  }

  onSwitchChange = (name) => (event, checked) => {
    if (name === "filter") {
      // filter change
      this.setState((prevState: State, props: TableContainerProps) => {
        return {
          filter: {
            filteringFunction: checked ? "&" : "|",
            filters: prevState.filter.filters,
          },
        };
      });
    } else {
      // active columns change
      this.setState((prevState: State, props: TableContainerProps) => {
        let activeColumnsNames = prevState.activeColumnsNames;
        if (checked) {
          if (!activeColumnsNames.includes(name)) {
            activeColumnsNames.push(name);
            this.updateColumnsCookie(name, true);
          }
        } else {
          if (activeColumnsNames.includes(name)) {
            activeColumnsNames = activeColumnsNames.filter((e) => e !== name);
            this.updateColumnsCookie(name, false);
          }
        }
        return {
          activeColumnsNames,
          activeColumns: this.filterActiveColumns(
            activeColumnsNames,
            props.columns
          ),
        };
      });
    }
  };

  changeSelection = (selection: Array<number>) => {
    const { onSelect } = this.props;
    const { rows } = this.state;
    this.setState({ selection });
    if (onSelect) {
      onSelect(selection.map((index) => ({ rowId: index, data: rows[index] })));
    }
  };

  render() {
    const {
      rows,
      activeColumns,
      activeColumnsNames,
      sorting,
      pageSize,
      allowedPageSizes,
      currentPage,
      totalCount,
      loading,
      downloading,
      filter,
      optionsMenuOpen,
      optionsMenuAnchorEl,
      exportMenuOpen,
      exportMenuAnchorEl,
    } = this.state;
    const {
      title,
      classes,
      columns,
      isUnpaginated,
      onSelect,
      selectedRows,
      defaultColumnWidths,
    } = this.props;
    const selection =
      selectedRows && selectedRows.length > 0
        ? selectedRows
        : this.state.selection;
    rows.forEach(function (row, index) {
      row.index = index;
    });
    const UnpaginatedTableTemplate = (
      <Grid rows={rows} columns={activeColumns}>
        {this.haveFilter() && (
          <FilteringState
            filters={filter.filters}
            onFiltersChange={this.changeFilters}
          />
        )}
        <IntegratedFiltering />
        <SortingState sorting={sorting} onSortingChange={this.changeSorting} />
        <IntegratedSorting />
        {onSelect && (
          <SelectionState
            selection={selection}
            onSelectionChange={this.changeSelection}
          />
        )}
        {onSelect && <IntegratedSelection />}
        <Table cellComponent={this.cellComponent} />
        <TableColumnResizing defaultColumnWidths={defaultColumnWidths} />
        <TableHeaderRow showSortingControls />
        {onSelect && <TableSelection showSelectAll highlightRow />}
        {this.haveFilter() && (
          <TableFilterRow cellComponent={FilterCellComponent} />
        )}
      </Grid>
    );

    const PaginatedTableTemplate = (
      <Grid rows={rows} columns={activeColumns}>
        {this.haveFilter() && (
          <FilteringState
            filters={filter.filters}
            onFiltersChange={this.changeFilters}
          />
        )}
        <SortingState sorting={sorting} onSortingChange={this.changeSorting} />
        <PagingState
          currentPage={currentPage}
          onCurrentPageChange={this.changeCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={this.changePageSize}
        />
        <CustomPaging totalCount={totalCount} />
        {onSelect && (
          <SelectionState
            selection={selection}
            onSelectionChange={this.changeSelection}
          />
        )}
        {onSelect && <IntegratedSelection />}
        <Table cellComponent={this.cellComponent} />
        <TableColumnResizing defaultColumnWidths={defaultColumnWidths} />
        <TableHeaderRow showSortingControls />
        {onSelect && <TableSelection showSelectAll highlightRow />}
        {this.haveFilter() && (
          <TableFilterRow cellComponent={FilterCellComponent} />
        )}
        <PagingPanel pageSizes={allowedPageSizes} />
      </Grid>
    );

    return (
      <div className={classes.root}>
        <Paper elevation={this.props.elevation}>
          <Toolbar className={classes.toolbar}>
            <Typography variant="h6" color="inherit" className={classes.flex}>
              {title}
            </Typography>
            {!this.props.hideClearFilter && (
              <Tooltip id="tooltip-clear" title="Clear Filters" placement="top">
                <IconButton
                  aria-label="Clear Filters"
                  aria-haspopup="true"
                  onClick={this.clearFilters}
                >
                  <YoutubeSearchedFor />
                </IconButton>
              </Tooltip>
            )}
            {this.props.exportUrl && (
              <div>
                <Tooltip id="tooltip-export" title="Export" placement="top">
                  <IconButton
                    aria-label="Export"
                    aria-haspopup="true"
                    onClick={this.handleExportClick}
                  >
                    <CloudDownload />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={exportMenuOpen}
                  anchorEl={exportMenuAnchorEl}
                  onClose={this.handleExportClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                >
                  <div className={classes.exportPopover}>
                    <Export onClick={this.loadExport} loading={downloading} />
                  </div>
                </Popover>
              </div>
            )}
            <Tooltip id="tooltip-settings" title="Settings" placement="top">
              <IconButton
                aria-label="Settings"
                aria-owns={optionsMenuOpen ? `table-${title}-menu` : null}
                aria-haspopup="true"
                onClick={this.handleOptionsClick}
                onClose={this.handleOptionsClose}
                data-test="settings-icon"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id={`table-${title}-menu`}
              anchorEl={optionsMenuAnchorEl}
              open={optionsMenuOpen}
              onClose={this.handleOptionsClose}
              PaperProps={{ style: { width: 300 } }}
            >
              <TableOptions
                filterChecked={filter.filteringFunction === "&"}
                onSwitchChange={this.onSwitchChange}
                activeColumnsNames={activeColumnsNames}
                columns={columns}
              />
            </Menu>
          </Toolbar>
        </Paper>
        <Paper elevation={this.props.elevation} data-test="table-first-row">
          {isUnpaginated ? UnpaginatedTableTemplate : PaginatedTableTemplate}
          {loading && <LoaderAbsolute />}
        </Paper>
      </div>
    );
  }
}

export default withStyles(styles)(TableContainer);
